// App.js
import React, { useState, useEffect, useRef } from "react";
import {
    BrowserRouter as Router,
    Route,
    Routes,
    Link,
    NavLink,
} from "react-router-dom";
import MainPage from "./components/MainPage";
import AgentsPage from "./components/AgentsPage";
import ContactDetails from "./components/ContactDetails";
import AgentView from "./components/AgentView";
import { getUsername, logOut } from "./auth";
import IntentsPage from "./components/IntentsPage";
import IntentView from "./components/IntentView";
import FixedTaxonomyPage from "./components/FixedTaxonomyPage";
import { HealthProvider } from "./contexts/HealthContext";
import HealthStatusPage from "./components/HealthStatusPage";
import HealthStatusIcon from "./components/HealthStatusIcon";
import HealthStatusAdminPage from "./components/HealthStatusAdminPage";
import QualityManagementPage from "./components/QualityManagementPage";

import { FilterProvider } from "./contexts/FilterContext";
import ScorecardPage from "./components/ScorecardPage";

function App() {
    const [username, setUsername] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const signOutRef = useRef(null);

    const toggleDropdown = () => {
        setShowDropdown(!showDropdown);
    };

    useEffect(() => {
        const username = getUsername();

        const underscoreIndex = username.indexOf("_");

        if (underscoreIndex === -1) {
            setUsername(username);
        } else {
            setUsername(username.substring(underscoreIndex + 1));
        }

        const handleClickOutside = (event) => {
            if (signOutRef.current &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !signOutRef.current.contains(event.target)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <HealthProvider>
            <FilterProvider>
                <Router>
                    <div className="flex flex-col min-h-screen">
                        {/* Navbar */}
                        <nav className="usan-primary shadow-lg">
                            <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
                                <div className="flex justify-between h-16 items-center">
                                    <div className="flex-shrink-0 flex items-center">
                                        <Link to="/">
                                            <img
                                                className="h-8 w-auto"
                                                src="/usan_small_white_logo.png"
                                                alt="USAN Logo"
                                            />
                                        </Link>
                                    </div>
                                    <div className="flex-shrink-0 flex items-center">
                                        <h1 className="text-white text-2xl font-semibold ml-2">
                                            AI Contact Analytics
                                        </h1>
                                    </div>
                                    <div className="flex">
                                        {[
                                            "/",
                                            "/agents",
                                            "/intents",
                                            ...(window.appSettings.fixedTaxonomyEnabled
                                                ? ["/fixed-taxonomy"]
                                                : []),
                                            ...(window.appSettings.qualityManagementEnabled
                                                ? ["/quality-management"]
                                                : []),
                                        ].map((path, index) => (
                                            <NavLink
                                                key={path}
                                                to={path}
                                                className={({ isActive }) =>
                                                    `${index !== 0 ? "ml-8 " : ""
                                                    }inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive
                                                        ? "border-white text-white"
                                                        : "border-transparent text-stone-200 hover:border-stone-200 hover:text-stone-50"
                                                    }`
                                                }
                                            >
                                                {path === "/"
                                                    ? "OVERVIEW"
                                                    : path === "/fixed-taxonomy"
                                                        ? "FIXED INTENTS"
                                                        : path === "/quality-management"
                                                            ? "QUALITY MANAGEMENT"
                                                            : path.slice(1).toUpperCase()}
                                            </NavLink>
                                        ))}
                                    </div>
                                    <div className="flex items-center">
                                        {/* Use the shared summary data in the icon */}
                                        <HealthStatusIcon />
                                        <div className="ml-3 relative">
                                            <div>
                                                <button
                                                    type="button"
                                                    className="flex items-center text-sm focus:outline-none"
                                                    onClick={toggleDropdown}
                                                    ref={dropdownRef}
                                                >
                                                    <span className="text-white font-bold mr-2">
                                                        {username}
                                                    </span>
                                                    <svg
                                                        className="h-5 w-5 text-white"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        aria-hidden="true"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </button>
                                                {showDropdown && (
                                                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                        <button
                                                            type="button"
                                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                            role="menuitem"
                                                            onClick={logOut}
                                                            ref={signOutRef}
                                                        >
                                                            Sign out
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </nav>

                        {/* Main content */}
                        <main className="flex-grow">
                            <Routes>
                                <Route path="/" element={<MainPage />} />
                                <Route path="/agents" element={<AgentsPage />} />
                                <Route path="/contact/:id" element={<ContactDetails />} />
                                <Route path="/scorecard/:id" element={<ScorecardPage />} />
                                <Route path="/agent/:agentName" element={<AgentView />} />
                                <Route path="/intents" element={<IntentsPage />} />
                                <Route path="/fixed-taxonomy" element={<FixedTaxonomyPage />} />
                                <Route path="/quality-management" element={<QualityManagementPage />} />
                                <Route path="/intent/:intentName" element={<IntentView />} />
                                <Route path="/health-status" element={<HealthStatusPage />} />
                                <Route path="/health-status-admin" element={<HealthStatusAdminPage />} />
                            </Routes>
                        </main>

                        {/* Footer */}
                        <footer className="usan-footer text-gray-600">
                            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                                <div className="flex flex-col md:flex-row justify-center items-center">
                                    <div className="mb-4 mr-10 md:mb-0">
                                        <p>
                                            &copy; {new Date().getFullYear()} USAN. All Rights Reserved.
                                        </p>
                                    </div>
                                    <div>
                                        <Link
                                            to="https://usan.com/support"
                                            className="text-gray-500 hover:text-gray-800 mr-4"
                                        >
                                            Support
                                        </Link>
                                        <Link
                                            to="https://usan.com/privacy-policy"
                                            className="text-gray-500 hover:text-gray-800"
                                        >
                                            Privacy Policy
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </footer>
                    </div>
                </Router>
            </FilterProvider>
        </HealthProvider>
    );
}

export default App;
